import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Landmark, Euro, Calendar, User, Home, CheckCircle2, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface MatchSuggestionCardProps {
  reconciliation: any;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onManualMatch: (transactionId: string) => void;
  isLoading?: boolean;
}

export function MatchSuggestionCard({
  reconciliation,
  onConfirm,
  onReject,
  onManualMatch,
  isLoading = false,
}: MatchSuggestionCardProps) {
  const transaction = reconciliation.bank_transactions;
  const rentPeriod = reconciliation.rent_periods;
  const lease = rentPeriod?.leases;
  const tenant = lease?.tenants;
  const property = lease?.properties;
  const sci = property?.scis;

  const getConfidenceBadge = (score: number) => {
    if (score >= 90) {
      return <Badge className="bg-green-600">Élevée ({score}%)</Badge>;
    }
    if (score >= 60) {
      return <Badge className="bg-yellow-600">Moyenne ({score}%)</Badge>;
    }
    return <Badge variant="destructive">Faible ({score}%)</Badge>;
  };

  const matchFactors = reconciliation.match_factors || {};

  return (
    <Card className="border-2 hover:border-primary/50 transition-colors">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            {/* Transaction Header */}
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1 font-bold">
                <Building2 className="h-4 w-4 text-primary" />
                {transaction?.bank_accounts?.scis?.name}
              </div>
              <div className="text-muted-foreground">|</div>
              <div className="flex items-center gap-1">
                <Landmark className="h-4 w-4 text-muted-foreground" />
                {transaction?.bank_accounts?.account_name}
                {transaction?.bank_accounts?.iban &&
                  ` (...${transaction.bank_accounts.iban.slice(-4)})`
                }
              </div>
            </div>

            {/* Transaction Amount & Date */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 text-2xl font-bold text-green-600">
                <Euro className="h-5 w-5" />
                +{transaction?.amount?.toFixed(2)} €
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {format(new Date(transaction?.transaction_date), 'dd/MM/yyyy', { locale: fr })}
              </div>
            </div>

            {transaction?.description && (
              <p className="text-sm text-muted-foreground">{transaction.description}</p>
            )}
          </div>

          {/* Confidence Score */}
          <div className="flex flex-col items-end gap-2">
            {getConfidenceBadge(reconciliation.confidence_score || 0)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Suggested Match */}
        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            Correspondance suggérée
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">Locataire</p>
              <p className="font-medium">
                {tenant?.tenant_type === 'professional' && tenant?.company_name
                  ? tenant.company_name
                  : `${tenant?.first_name} ${tenant?.last_name}`
                }
              </p>
            </div>

            <div>
              <p className="text-muted-foreground">Bien</p>
              <p className="font-medium flex items-center gap-1">
                <Home className="h-3 w-3" />
                {property?.name || property?.address}
              </p>
            </div>

            <div>
              <p className="text-muted-foreground">SCI</p>
              <p className="font-bold">{sci?.name}</p>
            </div>

            <div>
              <p className="text-muted-foreground">Période</p>
              <p className="font-medium">
                {format(new Date(rentPeriod?.period_start), 'MM/yyyy', { locale: fr })}
              </p>
            </div>

            <div>
              <p className="text-muted-foreground">Montant dû</p>
              <p className="font-bold">{rentPeriod?.total_amount?.toFixed(2)} €</p>
            </div>

            <div>
              <p className="text-muted-foreground">Différence</p>
              <p className={`font-medium ${
                Math.abs((transaction?.amount || 0) - (rentPeriod?.total_amount || 0)) < 0.01
                  ? 'text-green-600'
                  : 'text-orange-600'
              }`}>
                {((transaction?.amount || 0) - (rentPeriod?.total_amount || 0)).toFixed(2)} €
              </p>
            </div>
          </div>
        </div>

        {/* Match Factors */}
        {Object.keys(matchFactors).length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Facteurs de correspondance</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {matchFactors.exact_amount && (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="h-3 w-3" />
                  Montant exact ({matchFactors.exact_amount_score || 50})
                </div>
              )}
              {matchFactors.date_proximity && (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="h-3 w-3" />
                  Date proche ({matchFactors.date_score || 30})
                </div>
              )}
              {matchFactors.same_sci && (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="h-3 w-3" />
                  Même SCI ({matchFactors.sci_score || 20})
                </div>
              )}
              {matchFactors.tenant_name_match && (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="h-3 w-3" />
                  Nom locataire ({matchFactors.name_score || 15})
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => onConfirm(reconciliation.id)}
            disabled={isLoading}
            className="flex-1"
          >
            <Check className="mr-2 h-4 w-4" />
            Confirmer
          </Button>
          <Button
            onClick={() => onReject(reconciliation.id)}
            disabled={isLoading}
            variant="outline"
            className="flex-1"
          >
            <X className="mr-2 h-4 w-4" />
            Rejeter
          </Button>
          <Button
            onClick={() => onManualMatch(transaction?.id)}
            disabled={isLoading}
            variant="secondary"
          >
            Rapprocher manuellement
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
